import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Card, Edition, PriceConditions, GradedPrices } from './types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format price as currency
export function formatPrice(price: number | undefined | null): string {
  if (price == null) return '-';
  return `$${price.toFixed(2)}`;
}

// Calculate buy offer price
export function calculateBuyPrice(marketPrice: number, offerPercent: number): number {
  return marketPrice * (offerPercent / 100);
}

// Debounce function
export function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

// High-value card thresholds
const HIGH_VALUE_THRESHOLD = 50;   // Magenta border (static glow)
const ULTRA_VALUE_THRESHOLD = 100; // Magenta pulsing glow

// Determine the value tier of a card based on its price
export type ValueTier = 'standard' | 'high' | 'ultra';

export function getValueTier(price: number | undefined | null): ValueTier {
  if (price == null || price < HIGH_VALUE_THRESHOLD) {
    return 'standard';
  }
  if (price >= ULTRA_VALUE_THRESHOLD) {
    return 'ultra';
  }
  return 'high';
}

// Get the CSS class for high-value glow effect
export function getValueGlowClass(price: number | undefined | null): string {
  const tier = getValueTier(price);
  switch (tier) {
    case 'ultra':
      return 'glow-ultra-value';
    case 'high':
      return 'glow-high-value';
    default:
      return '';
  }
}

// Get stagger animation class for card grid
export function getStaggerClass(index: number): string {
  // Cap at 12 for stagger classes (defined in CSS)
  const staggerIndex = Math.min(index + 1, 12);
  return `card-stagger-${staggerIndex}`;
}

// Edition utility functions for vintage Pokemon cards

export function getDefaultEdition(card: Card): Edition | null {
  if (!card.has_editions || !card.available_editions?.length) return null;
  return card.available_editions[0]; // Already ordered: 1st > shadowless > unlimited
}

export function getActivePrices(card: Card, edition: Edition | null): PriceConditions | undefined {
  if (!edition || !card.edition_prices) return card.prices;
  return card.edition_prices[edition] ?? card.prices;
}

export function getActiveGradedPrices(card: Card, edition: Edition | null): GradedPrices | undefined {
  if (!edition || !card.edition_graded_prices) return card.graded_prices;
  return card.edition_graded_prices[edition] ?? card.graded_prices;
}

export function getEditionLabel(edition: Edition): string {
  return edition === '1st' ? '1st' : edition === 'shadowless' ? 'SHL' : 'UNL';
}

export function getEditionFullName(edition: Edition): string {
  return edition === '1st' ? '1st Edition' : edition === 'shadowless' ? 'Shadowless' : 'Unlimited';
}

// Format a timestamp as relative time (e.g., "2h ago", "5m ago")
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    return `${diffDays}d ago`;
  }
  if (diffHours > 0) {
    return `${diffHours}h ago`;
  }
  if (diffMinutes > 0) {
    return `${diffMinutes}m ago`;
  }
  return 'just now';
}

// Hybrid format: relative under 24h ("2h ago"), absolute past 24h ("Apr 28").
// Operators can scan freshness in <1s; absolute past a day stays precise without
// "5d ago" math. Input is Unix seconds (JustTCG variant.lastUpdated format).
export function formatPriceTimestamp(unixSeconds: number): string {
  const ms = unixSeconds * 1000;
  const diffMs = Date.now() - ms;
  // Future timestamps (clock skew) collapse to "just now" rather than negative.
  if (diffMs < 24 * 60 * 60 * 1000) {
    return formatRelativeTime(ms);
  }
  const date = new Date(ms);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}
